//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import angular from 'angular';
import { BoundActionCreatorMap } from 'redux-extensions';
import { INgRedux } from 'ng-redux';

import { getItemsMetadata } from '@apis/bulkActionsApi';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { StreamsLaunchContext } from '@streams-drawer/streams-drawer-constants';
import { IBulkActionsActions } from 'app/bulk-actions/bulk-actions-actions/bulk-actions-actions';
import template from './streams-drawer-top-bar.html';
import './streams-drawer-top-bar.less';
import { BulkActionNames } from 'app/bulk-actions/model/BulkActionNames';
import { setSelectedStreamQueryParam } from '@streams-drawer/utils/setSelectedStreamQueryParam';
import { muteSelectedItems } from '@apis/video-processing/videoProcessingApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import './item-removal-confirmation-modal/item-removal-confirmation-modal';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { muteCaptionDisclaimerMessage } from 'app/constants/mute-tooltip-content';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

export interface StreamsDrawerTopBar
  extends BoundActionCreatorMap<IBulkActionsActions> {}
export class StreamsDrawerTopBar {
  public streamsLaunchContext = StreamsLaunchContext;
  public hasUnsavedAssetFeedFilters: boolean;
  public isAssetFeedsOnboarding: boolean;
  public onClearSelectedItems: () => any;
  public onClickCaptionTurnOnOff: (shouldCaption: boolean) => any;
  public onConfirmAssetFeedsDrawerClose: () => any;
  public onCloseStreamsDrawer: () => any;
  public onCompleteAssetFeedsOnboarding: () => any;
  public reduxUnsubscribe: () => void;
  public isVideoCaptionsFeatureEnabled: boolean;
  public isVideoActionInProgress: boolean;
  private selectedItems: ISelectedItem[];

  public muteCaptionDisclaimerMessage = muteCaptionDisclaimerMessage;

  constructor(
    private $ngRedux: INgRedux,
    BulkActionsActions: IBulkActionsActions,
  ) {
    this.reduxUnsubscribe = this.$ngRedux.connect(
      null,
      BulkActionsActions,
    )(this);
  }

  public onCloseClick = () => {
    this.onClearSelectedItems();
    //Remove the openStreamId param from the URL
    setSelectedStreamQueryParam(undefined);
    if (this.hasUnsavedAssetFeedFilters) {
      this.onConfirmAssetFeedsDrawerClose();
    } else {
      if (this.isAssetFeedsOnboarding) {
        this.onCompleteAssetFeedsOnboarding();
      }
      this.onCloseStreamsDrawer();
    }
  };

  public onCaptionClick = (shouldCaption: boolean) => {
    this.isVideoActionInProgress = true;
    this.onClickCaptionTurnOnOff(shouldCaption);
  };

  public onAddLabelsClick = () => {
    this.openAddLabelsModal();
  };
  public async clickMuteUnmuteSelectedItems(isMuting: boolean) {
    const cuids: PartialCuid[] = this.selectedItems.map(
      (obj: { cuid: PartialCuid }) => {
        return obj.cuid;
      },
    );
    const videoExists = await getItemsMetadata(cuids).then((metaItem) => {
      return metaItem
        .filter((item) => isDefined(item.videoSource))
        .map((item) => {
          return Cuid.toPartial(item.rootCuid);
        });
    });
    if (videoExists.length > 0) {
      try {
        await muteSelectedItems(isMuting, videoExists).then((res) => {
          const muteStatus = isMuting ? 'muting' : 'unmuting';
          this.muteMessages(
            'Success',
            ToastType.SUCCESS,
            `Request for ${muteStatus} successfully submitted.`,
          );
        });
      } catch (_err: any) {
        const muteStatus = isMuting ? 'muting' : 'unmuting';

        this.muteMessages('Error', ToastType.ERROR, `Error ${muteStatus}.`);
      }
    } else {
      const muteValue = isMuting ? 'muted' : 'unmuted';
      this.muteMessages(
        'Error',
        ToastType.ERROR,
        `Image cannot be ${muteValue}.`,
      );
    }
  }
  public muteMessages(title: string, type: ToastType, message: any) {
    this.$ngRedux.dispatch(
      toastNotificationsActions.addToastNotification({
        title: title,
        type: type,
        message: message,
      }),
    );
  }

  public $onDestroy() {
    this.reduxUnsubscribe();
  }
}

angular.module('curalate').component('streamsDrawerTopBar', {
  bindings: {
    launchContext: '<',
    hasUnsavedAssetFeedFilters: '<',
    isAssetFeedsOnboarding: '<',
    isAssetFeedStreamUpdatingFilters: '<',
    isRemovalConfirmationModalOpen: '<',
    onAddItems: '&',
    onClearSelectedItems: '&',
    onClickCaptionTurnOnOff: '&',
    onCloseRemovalConfirmationModal: '&',
    onCloseStreamsDrawer: '&',
    onConfirmAssetFeedsDrawerClose: '&',
    onCompleteAssetFeedsOnboarding: '&',
    onOpenRemovalConfirmationModal: '&',
    onRemoveItems: '&',
    onSaveAssetFeedsFilters: '&',
    selectedItems: '<',
    isVideoActionInProgress: '<',
    selectedStreamType: '<',
    isVideoCaptionsFeatureEnabled: '<',
    streams: '<',
  },
  controller: ['$ngRedux', 'BulkActionsActions', StreamsDrawerTopBar],
  template,
});
